import type { InvestorIndexPublicationData } from '@workspace/api-backend'
import { Badge } from '@workspace/ui/components/badge'
import type { TableColumn } from '@workspace/ui/components/data-table'
import { CellText, DataTable } from '@workspace/ui/components/data-table'
import { formatDateTime } from '../lib/format'

const columns: TableColumn<InvestorIndexPublicationData>[] = [
  {
    header: 'Published',
    width: '230px',
    render: (row) => {
      const by = row.publishedByName || row.publishedByService || '—'
      return (
        <div className="flex flex-col gap-1">
          <CellText className="whitespace-nowrap">{formatDateTime(row.createdAt)}</CellText>
          <div className="flex min-w-0 items-center gap-2">
            {/* A service publish is the machine path (X-Service-Token); everything else came through this page. */}
            <Badge variant={row.publishedByService ? 'outline' : 'secondary'} size="sm">
              {row.publishedByService ? 'API' : 'Admin upload'}
            </Badge>
            <CellText className="truncate text-xs">
              <span title={by}>{by}</span>
            </CellText>
          </div>
        </div>
      )
    },
  },
  {
    header: 'Certified',
    width: '240px',
    render: (row) => (
      <div className="flex items-center gap-2 whitespace-nowrap">
        <CellText>{row.certificationDate}</CellText>
        <Badge variant={row.certificationStatus === 'CERTIFIED' ? 'success' : 'warning'} size="sm">
          {row.certificationStatus === 'CERTIFIED' ? 'Certified' : 'With quarantine'}
        </Badge>
      </div>
    ),
  },
  {
    header: 'Pool',
    width: '130px',
    render: (row) => (
      <span className="font-mono text-xs text-muted-foreground">{row.poolSha256.slice(0, 12)}</span>
    ),
  },
  {
    header: 'Pages',
    render: (row) => (
      <div className="flex flex-col whitespace-nowrap">
        <CellText>
          {row.listPages} lists · {row.fundPages} funds
        </CellText>
        <CellText className="text-xs">{row.publicRows} rows</CellText>
      </div>
    ),
  },
  {
    header: 'Result',
    width: '150px',
    render: (row) => (
      <div className="flex flex-col whitespace-nowrap">
        <CellText>{row.upserted} upserted</CellText>
        <CellText className="text-xs">{row.depublished} de-published</CellText>
      </div>
    ),
  },
]

export function PublicationHistory({
  publications,
  isLoading,
}: {
  publications: InvestorIndexPublicationData[]
  isLoading: boolean
}) {
  return (
    <DataTable
      columns={columns}
      data={publications}
      isLoading={isLoading}
      emptyMessage="Nothing published yet."
      keyExtractor={(row) => row.id}
      minWidth="860px"
    />
  )
}
